import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useHistory, Link, useLocation, Redirect } from "react-router-dom";
import ReactGA from "react-ga";
import classes from "../TrucksAvailable/TrucksAvailable.module.css";
import classes1 from "../Indent/IndentMain.module.css";
import { Activity, currentRequestTimeStamp, isMobile } from "../../Constants";
import Unauthorized from "../Unauthorized";
import { Row } from "../Shared/FlexDiv/FlexDiv";
import IndentRoute from "./IndentRoute";
import Modal from "../Shared/PopupModal/PopupModal";
import ConfigureHeader from "../../utils/ConfigureHeader";
import HeaderMap from "../../utils/HeaderMap";
import { UserApiContext } from "../UserDetailsContext/UserDetailsContext";
import { MyCompanyProfileContext } from "../../MyCompanyProfileContext";
import { useIndentTracking } from "./hooks/useIndentTracking";
import { toast } from 'react-toastify';
import useViewOnlyAccess from "../../hooks/useViewOnlyAccess";
import ModuleHeading from "../Shared/ModuleHeading/ModuleHeading";

const IndentMain = () => {
  const history = useHistory();
  const location = useLocation();

  // Contexts
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData, fetchCompanyProfileDetails } = useContext(
    MyCompanyProfileContext
  );

  const { trackIndentEvent } = useIndentTracking();

  const { isRestricted: rt, hasModuleAccess: trackAccess, noAccess: trackNoAccess, dashboardAccess: trackDashboardAccess, loading: trackLoading } = useViewOnlyAccess('indent', 'lorri_indent_tracking', true);
  const { isRestricted: rr, hasModuleAccess: reqAccess, noAccess: reqNoAccess, dashboardAccess: reqDashboardAccess, loading: reqLoading } = useViewOnlyAccess('indent', 'lorri_indent_request', true);
  const isRestricted = rt || rr;
  const dashboardLocked = !reqDashboardAccess && !trackDashboardAccess;

  const isDashboardAllowedByPolicy =
    userDetails?.policy?.indents?.allowed_dashboard === "yes";

  // States
  const [policy, setPolicy] = useState(null);
  const [userContent, setUserContent] = useState(null);
  const [step, setStep] = useState(0);
  const [formats, setFormats] = useState([]);
  const [finalKeys, setFinalKeys] = useState({});
  const [selectedIndex, setSelectedIndex] = useState();
  const [data, setData] = useState([]);
  const [file, setFile] = useState(null);
  const [show, setShow] = useState(false);

    useEffect(() => {
  const headerLink = document.querySelector('.nav-link[data-section="indent"]');
  if (headerLink) {
    document
      .querySelectorAll(".nav-link")
      .forEach((el) => el.classList.remove("active"));
    headerLink.classList.add("active");
  }

  return () => {
    const headerLink = document.querySelector('.nav-link[data-section="indent"]');
    if (headerLink) headerLink.classList.remove("active");
  };
}, []);

  useEffect(() => {
    ReactGA.pageview("Indent");

    Activity([
      {
        screen: "Indents",
        module: "Indents",
        user_activity: "Indent page visited",
        timestamp: currentRequestTimeStamp(),
      },
    ]);

    trackIndentEvent("indent", "user has visited indent module");

    const userPolicy = userDetails?.policy;
    setPolicy(userPolicy);

    if (userPolicy?.indents?.allowed !== "yes") {
      setUserContent(<Unauthorized />);
    }
  }, [userDetails]);

  // Handle unauthorized access separately before rendering
  const accessDenied = !trackLoading && !reqLoading && trackAccess === false && reqAccess === false;

  // Effect to load formats
  useEffect(() => {
    const formats = companySettingData?.indent?.upload_formats;
    if (Array.isArray(formats) && formats.length > 0) {
      setFormats(formats);
    }
  }, [companySettingData?.indent?.upload_formats]);

  // CALLBACKS
  const resetStates = useCallback(() => {
    setStep(0);
    setFormats([]);
    setFile(null);
    setData([]);
    setFinalKeys({});
  }, []);

  const handleNavigation = useCallback(
    (path, screen, activity) => {
      Activity([
        {
          screen: "Indents",
          module: screen,
          user_activity: activity,
          timestamp: currentRequestTimeStamp(),
        },
      ]);

      trackIndentEvent(screen, activity);

      history.push({ pathname: path });
    },
    [history]
  );

  const handleCloseModal = useCallback(() => {
    const hasNewKey = formats.some((item) => "new" in item);
    if (hasNewKey) {
      if (
        window.confirm(
          "The changes are not saved, are you sure you want to close?"
        )
      ) {
        resetStates();
        fetchCompanyProfileDetails();
        return true;
      }
      return false;
    }
    setStep(0);
    return true;
  }, [formats, fetchCompanyProfileDetails, resetStates]);

  const allTabs = useMemo(
    () => [
      {
        path: "/indents/requestdashboard",
        label: "Dashboard",
        screen: "indent dashboard",
        activity: "user has clicked on indent dashboard tab",
        disabled: dashboardLocked,
        policyHidden: !isDashboardAllowedByPolicy,
      },
      {
        path: "/indentrequest",
        label: "Indent Request",
        screen: "indent request",
        activity: "user has clicked on indent request tab",
        disabled: reqNoAccess
      },
      {
        path: "/indenttracking",
        label: "Indent Tracking",
        screen: "indent tracking",
        activity: "user has clicked on indent tracking tab",
        disabled: trackNoAccess
      },
    ],
    [reqNoAccess, trackNoAccess, dashboardLocked, isDashboardAllowedByPolicy]
  );

  const tabs = useMemo(
    () => allTabs.filter((t) => !t.policyHidden),
    [allTabs]
  );

  if (accessDenied) {
    return (
      <Unauthorized
        message="You dont have access for this module"
        showPremiumButton={false}
      />
    );
  }

  if (policy?.indents?.allowed !== "yes") return userContent;

  const activePath = location.pathname;
  const isRequestView = activePath.includes("request");
  const activeRestricted = isRequestView ? rr : rt;
  const activeModuleLabel = isRequestView ? "Indent Request" : "Indent Tracking";

  // Safe redirect logic
  let redirectPath = null;
  if (!trackLoading && !reqLoading) {
    if (!isDashboardAllowedByPolicy && location.pathname === "/indents/requestdashboard") {
      redirectPath = !reqNoAccess ? "/indentrequest" : (!trackNoAccess ? "/indenttracking" : null);
    } else if (dashboardLocked && location.pathname === "/indents/requestdashboard") {
      redirectPath = !reqNoAccess ? "/indentrequest" : (!trackNoAccess ? "/indenttracking" : null);
    } else if (reqNoAccess && location.pathname === "/indentrequest") {
      redirectPath = !trackNoAccess ? "/indenttracking" : "/indents/requestdashboard";
    } else if (trackNoAccess && location.pathname === "/indenttracking") {
      redirectPath = !reqNoAccess ? "/indentrequest" : "/indents/requestdashboard";
    }
  }

  // Prevent redirect loops if redirectPath is same as current path or null
  if (redirectPath && redirectPath !== location.pathname) {
    return <Redirect to={redirectPath} />;
  }

  const renderTabs = () => (
    <ul
      className="nav nav-tabs"
      id="myTab"
      role="tablist"
      style={{ paddingTop: "1%", width: "80%", textAlign: "center" }}
    >
      {tabs.map(({ path, label, screen, activity, disabled }) => (
        <li className="nav-item" key={path}>
          <div
            className={`nav-link ${window.location.pathname === path ? "active" : ""
              }`}
          >
            <Link
              className={`btn btn-link ${disabled ? classes1['restricted-link'] : ''}`}
              onClick={() => !disabled && handleNavigation(path, screen, activity)}
              to={!disabled ? { pathname: path } : '#'}
              style={{
                color: "#393185",
                fontSize: "17px",
                fontWeight: "bolder",
              }}
            >
              {label} {disabled && <i className="fa fa-lock ms-2" />}
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <ModuleHeading title={"LoRRI Indent"} viewOnly={activeRestricted} />
      </div>

      {!isMobile() ? (
        <div style={{ width: "90%", margin: "auto", position: "relative" }}>
          <div className={`row ${classes1["indentMargin"]}`}>
            {renderTabs()}

          {location.pathname === "/indentrequest" && (
            <Modal
              moduleName="Add Indents"
              screenName="Indent"
              buttonClass="btn btn-sm btn-primary"
              buttonLabel="Header Configuration"
              style={{}}
              title={
                step === 0
                  ? "Uploaded Formats:"
                  : "View and Customise Header Format"
              }
              header={
                step === 0
                  ? "Uploaded Formats:"
                  : "View and Customise Header Format"
              }
              overflowStyles={{ overflow: "hidden" }}
              content={
                step === 0 ? (
                  <ConfigureHeader
                    setFormats={setFormats}
                    setFinalKeys={setFinalKeys}
                    setSelectedIndex={setSelectedIndex}
                    setStep={setStep}
                    setData={setData}
                    setFile={setFile}
                    show={show}
                    formats={formats}
                  />
                ) : (
                  <HeaderMap
                    isConfigureScreen
                    setFormats={setFormats}
                    setFinalKeys={setFinalKeys}
                    setStep={setStep}
                    data={data}
                    formats={formats}
                    finalKeys={finalKeys}
                    selectedIndex={selectedIndex}
                  />
                )
              }
              onClose={handleCloseModal}
              size="lg"
              disabled={rr}
            />
          )}
          </div>
          <IndentRoute />
        </div>
      ) : (
        <div>
          <div
            style={{
              display: 'flex',
              width: '100%',
              borderBottom: '2px solid #e0e0e0',
              backgroundColor: '#fff',
              position: 'sticky',
              top: 0,
              zIndex: 20,
            }}
          >
            {tabs.map(({ label, path, screen, activity, disabled }, index) => {
              const isActive = window.location.pathname === path;
              return (
                <button
                  key={path}
                  type="button"
                  disabled={disabled}
                  style={{
                    flex: 1,
                    padding: '10px 4px',
                    fontSize: '13px',
                    fontWeight: isActive ? '700' : '500',
                    color: isActive ? '#393186' : '#666',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: isActive ? '3px solid #393186' : '3px solid transparent',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    transition: 'all 0.2s',
                    marginBottom: '-2px',
                  }}
                  onClick={() => !disabled && handleNavigation(path, screen, activity)}
                >
                  {label} {disabled && <i className="fa fa-lock" style={{ fontSize: '10px' }} />}
                </button>
              );
            })}
          </div>
          <IndentRoute />
        </div>
      )}
    </div>
  );
};

export default IndentMain;
